import {
  ADMISSIONS_ACTION_SCREENS,
  ADMISSIONS_INFO_SCREENS,
  ADMISSIONS_STEPS,
  REHAB_FORM_APPROVAL_PAGE,
} from ".";

export const PARAM = "([^/]+)";
export const PARAMS = {
  ACCOUNT_ID: ":account_id",
  ADMISSIONS_ID: ":admissions_id",
  AUCTION_ID: ":auction_id",
  AUCTION_REQUEST_ID: ":auction_request_id",
  CAREPROVIDER_ID: ":careprovider_id",
  CARESEEKER_ID: ":careseeker_id",
  CHAT_ID: ":chat_id",
  CONSULTANT_ID: ":consultant_id",
  FORM: ":form",
  PATIENT_ID: ":patient_id",
  PROVIDER_SEARCH: ":provider_search",
  SECTION: ":section",
  STEP: ":step",
  TAB: ":tab",
  TOKEN: ":token",
} as const;

const CATEGORIES: Record<string, Capitalize<string>> = {
  ADMISSIONS: "Admissions",
  ASSESSMENT: "Assessment",
  AUCTION: "Auction",
  AUTH: "Auth",
  CAREPROVIDER: "Careprovider",
  CHAT: "Chat",
  CLINIC: "Clinic",
  DASHBOARD: "Dashboard",
  ONBOARDING: "Onboarding",
  PASSWORD_RESET: "Password Reset",
  PATIENT: "Patient",
  PREDICT: "Predict",
  PROVIDER_SEARCH: "Provider Search",
  REQUEST: "Request",
  SETTINGS: "Settings",
  SPN_PROVIDER: "SPN Provider",
} as const;

/**
 * The page_name property used for tracking page views is a combination of the page category and page name.
 * For example, if the page category is "Assessment" and the page name is "Medical",
 * the resulting page name value would be "Assessment Medical".
 * Each word must be capitalized.
 *
 */

const PAGES = {
  APP: {
    ASSESSMENT_ELECTIVE_REHAB_INFO: `${CATEGORIES.ASSESSMENT} Elective Rehab Info`,
    ASSESSMENT_ELECTIVE_REHAB_MEDICAL: `${CATEGORIES.ASSESSMENT} Elective Rehab Medical`,
    ASSESSMENT_HOME_CARE_INFO: `${CATEGORIES.ASSESSMENT} Home Care Info`,
    ASSESSMENT_HOME_CARE_SEARCH: `${CATEGORIES.ASSESSMENT} Home Care Search`,
    ASSESSMENT_INFO: `${CATEGORIES.ASSESSMENT} Info`,
    ASSESSMENT_MEDICAL: `${CATEGORIES.ASSESSMENT} Medical`,
    ASSESSMENT_MEDICAL_SUPPLIES_CATALOGUE: `${CATEGORIES.ASSESSMENT} Medical Supplies`,
    ASSESSMENT_MEDICAL_SUPPLIES_INFO: `${CATEGORIES.ASSESSMENT} Medical Supplies Info`,
    ASSESSMENT_MENTAL: `${CATEGORIES.ASSESSMENT} Mental`,
    ASSESSMENT_MOBILE_SHORT: `${CATEGORIES.ASSESSMENT} Mobile Short`,
    ASSESSMENT_MOBILITY: `${CATEGORIES.ASSESSMENT} Mobility`,
    ASSESSMENT_NEEDS: `${CATEGORIES.ASSESSMENT} Needs`,
    ASSESSMENT_PSYCH_EVALUATION: `${CATEGORIES.ASSESSMENT} Psych Evaluation`,
    ASSESSMENT_PSYCH_INFO: `${CATEGORIES.ASSESSMENT} Psych Info`,
    ASSESSMENT_SHORT_REHAB_INFO: `${CATEGORIES.ASSESSMENT} Rehab Short Info`,
    ASSESSMENT_SHORT_REHAB_MEDICAL: `${CATEGORIES.ASSESSMENT} Rehab Short Medical`,
    ASSESSMENT_STEP: `${CATEGORIES.ASSESSMENT} Step`,
    ASSESSMENT_TRANSFER: `${CATEGORIES.ASSESSMENT} Transfer`,
    ASSESSMENT_TRANSPORT: `${CATEGORIES.ASSESSMENT} Transport`,
    ASSESSMENT_TRANSPORT_INFO: `${CATEGORIES.ASSESSMENT} Transport Info`,
    ASSESSMENT_TRANSPORT_SEARCH: `${CATEGORIES.ASSESSMENT} Transport Search`,
    AUTH_LOGIN: `${CATEGORIES.AUTH} Login`,
    AUTH_PASSWORD_RESET: `${CATEGORIES.AUTH} Reset Password`,
    AUTH_SSO_LOGIN: `${CATEGORIES.AUTH} SSO Login`,
    CHAT: `${CATEGORIES.CHAT}`,
    CHAT_ID: `${CATEGORIES.CHAT} Details`,
    CLINIC_CHANGE_PASSWORD: `${CATEGORIES.CLINIC} Change Password`,
    CLINIC_DEBUG_ON_PREMISE_IMPORTS: `${CATEGORIES.CLINIC} Debug OnPremise Imports`,
    CLINIC_DEBUG_ON_PREMISE_INSTALL: `${CATEGORIES.CLINIC} Debug OnPremise Install`,
    CLINIC_DEBUG_ON_PREMISE_TEST: `${CATEGORIES.CLINIC} Debug OnPremise Test`,
    CLINIC_REPORT: `${CATEGORIES.CLINIC} Report`,
    CLINIC_USERS: `${CATEGORIES.CLINIC} User Management`,
    DASHBOARD_ACTIVE: `${CATEGORIES.DASHBOARD} Active`,
    DASHBOARD_ARCHIVED: `${CATEGORIES.DASHBOARD} Archived`,
    DASHBOARD_DRAFT: `${CATEGORIES.DASHBOARD} Draft`,
    DASHBOARD_FINISHED: `${CATEGORIES.DASHBOARD} Finished`,
    DASHBOARD_PRINTABLE: `${CATEGORIES.DASHBOARD} Printable`,
    PATIENT_BARTHEL_INDEX: `${CATEGORIES.PATIENT} Barthel Index`,
    PATIENT_CREATE: `${CATEGORIES.PATIENT} Create`,
    PATIENT_DISCHARGE_PRINT: `${CATEGORIES.CLINIC} Printable Discharge`,
    PATIENT_DOCS_REHAB_FORMS_HOME: `${CATEGORIES.PATIENT} Docs Rehab Forms Home`,
    PATIENT_EARLY_REHAB_INDEX: `${CATEGORIES.PATIENT} Early Rehab Index`,
    PATIENT_FILES: `${CATEGORIES.PATIENT} Files`,
    PATIENT_INSURANCE: `${CATEGORIES.PATIENT} Insurance`,
    PATIENT_MAP: `${CATEGORIES.PATIENT} Map`,
    PATIENT_PROFILE: `${CATEGORIES.PATIENT} Profile`,
    PATIENT_REHAB_FORMS: `${CATEGORIES.PATIENT} Rehab Forms`,
    PATIENT_REHAB_FORMS_HOME: `${CATEGORIES.PATIENT} Rehab Forms Home`,
    PATIENT_SEARCH: `${CATEGORIES.PATIENT} Search`,
    PATIENT_SEARCH_PANEL_PRINT: `${CATEGORIES.CLINIC} Printable SearchPanel`,
    PATIENT_TIMELINE: `${CATEGORIES.PATIENT} Timeline`,
    PATIENT_TRANSITIONAL_CARE: `${CATEGORIES.PATIENT} Transitional Care`,
    PREDICT: `${CATEGORIES.PREDICT}`,
    PREDICT_DETAILS: `${CATEGORIES.PREDICT} Details`,
    REHAB_FORM_APPROVAL: `${CATEGORIES.PATIENT} Rehab Forms Approval`,
    REQUEST_DETAILS: `${CATEGORIES.REQUEST} Details`,
  },
  PROVIDER_SEARCH: {
    ACCOUNTS: `${CATEGORIES.PROVIDER_SEARCH} My Account`,
    ACTIVATION_WIZARD: `${CATEGORIES.PROVIDER_SEARCH} Activation Wizard`,
    AUCTION_REQUEST: `${CATEGORIES.PROVIDER_SEARCH} Request Details`,
    CONSULTANT_DETAILS: `${CATEGORIES.PROVIDER_SEARCH} Consultant Details`,
    CONSULTANT_DETAILS_PRINT: `${CATEGORIES.PROVIDER_SEARCH} Consultant Details Print`,
    GLOSSARY: `${CATEGORIES.PROVIDER_SEARCH} Glossary`,
    LISTINGS: `${CATEGORIES.PROVIDER_SEARCH} Home Page`,
    LISTINGS_PRINT: `${CATEGORIES.PROVIDER_SEARCH} Home Page Print`,
    LOGIN_WIZARD: `${CATEGORIES.PROVIDER_SEARCH} Login Wizard`,
    LOGOUT: `${CATEGORIES.PROVIDER_SEARCH} Logout`,
    PATIENT_DASHBOARD: `${CATEGORIES.PROVIDER_SEARCH} Search Dashboard`,
    PROVIDER_DETAILS: `${CATEGORIES.PROVIDER_SEARCH} Provider Details`,
    PROVIDER_DETAILS_PRINT: `${CATEGORIES.PROVIDER_SEARCH} Provider Details Print`,
    ZIPCODE_SELECT: `${CATEGORIES.PROVIDER_SEARCH} Zipcode Select`,
  },
  RECEIVER: {
    ADMISSIONS_ACTION_PAGE_STOP_AUCTION: `${CATEGORIES.ADMISSIONS} Action Page Stop Auction`,
    ADMISSIONS_FORM_1_PERSONAL_INFORMATION: `${CATEGORIES.ADMISSIONS} Form Step 1 Personal Information`,
    ADMISSIONS_FORM_2_DESIRED_CARE: `${CATEGORIES.ADMISSIONS} Form Step 2 Desired Care`,
    ADMISSIONS_FORM_3_CARE_INFORMATION: `${CATEGORIES.ADMISSIONS} Form Step 3 Care Information`,
    ADMISSIONS_FORM_4_FURTHER_INFORMATION: `${CATEGORIES.ADMISSIONS} Form Step 4 Further Information`,
    ADMISSIONS_INFO_PAGE_ADD_REQUEST_SUCCESS: `${CATEGORIES.ADMISSIONS} Info Page Add Request Success`,
    ADMISSIONS_INFO_PAGE_EMAIL_SENT: `${CATEGORIES.ADMISSIONS} Info Page Email Sent`,
    ADMISSIONS_LOGIN_WIZARD: `${CATEGORIES.ADMISSIONS} Login Wizard`,
    ADMISSIONS_START_PAGE: `${CATEGORIES.ADMISSIONS} My Account`,
    AUTH_LOGIN: `${CATEGORIES.AUTH} Login`,
    AUTH_PASSWORD_RESET: `${CATEGORIES.AUTH} Password Reset`,
    CAREPROVIDER_NOTIFICATION_PANEL: `${CATEGORIES.CAREPROVIDER} Notification Panel`,
    CAREPROVIDER_PASSWORD_CHANGE: `${CATEGORIES.CAREPROVIDER} Change Password`,
    CAREPROVIDER_PROFILE: `${CATEGORIES.CAREPROVIDER} Profile`,
    CAREPROVIDER_REPORT: `${CATEGORIES.CAREPROVIDER} Report`,
    DASHBOARD_ATTRIBUTED: `${CATEGORIES.DASHBOARD} Attributed`,
    DASHBOARD_DIRECT: `${CATEGORIES.DASHBOARD} Direct`,
    DASHBOARD_IN_PROGRESS: `${CATEGORIES.DASHBOARD} In Progress`,
    DASHBOARD_NEW: `${CATEGORIES.DASHBOARD} New`,
    DASHBOARD_OTHER: `${CATEGORIES.DASHBOARD} Other`,
    OB_PERSONAL_CONGRATS: `${CATEGORIES.ONBOARDING} Personal Congratulations`,
    OB_PERSONAL_PASSWORD: `${CATEGORIES.ONBOARDING} Personal Password`,
    OB_PROVIDER_ACCOUNT: `${CATEGORIES.ONBOARDING} Provider Account`,
    OB_PROVIDER_COMPANY: `${CATEGORIES.ONBOARDING} Provider Company`,
    OB_PROVIDER_CONGRATS: `${CATEGORIES.ONBOARDING} Provider Congratulations`,
    OB_PROVIDER_INFO: `${CATEGORIES.ONBOARDING} Provider Additional Info`,
    OB_PROVIDER_PASSWORD: `${CATEGORIES.ONBOARDING} Provider Password`,
    OB_PROVIDER_SERVICES: `${CATEGORIES.ONBOARDING} Provider Services`,
    ONBOARDING_COMPANY: `${CATEGORIES.ONBOARDING} Company`,
    ONBOARDING_PERSONAL: `${CATEGORIES.ONBOARDING} Personal`,
    ONBOARDING_PROVIDER: `${CATEGORIES.ONBOARDING} Provider Welcome`,
    REQUEST_BARTHEL_INDEX: `${CATEGORIES.REQUEST} Barthel Index`,
    REQUEST_DETAILS: `${CATEGORIES.REQUEST} Details`,
    REQUEST_EARLY_REHAB: `${CATEGORIES.REQUEST} Early Rehab Index`,
    SETTINGS_ACCOUNT: `${CATEGORIES.SETTINGS} Account`,
    SETTINGS_CAPACITY: `${CATEGORIES.SETTINGS} Capacity`,
    SETTINGS_EXTENSIONS: `${CATEGORIES.SETTINGS} Extensions`,
    SETTINGS_LEGAL: `${CATEGORIES.SETTINGS} Legal`,
    SETTINGS_NOTIFICATIONS: `${CATEGORIES.SETTINGS} Notifications`,
    SETTINGS_PARTNER_CLINICS: `${CATEGORIES.SETTINGS} Partner Clinics`,
    SETTINGS_PROFILE: `${CATEGORIES.SETTINGS} Company Profile`,
    SETTINGS_PWA_BETA: `${CATEGORIES.SETTINGS} PWA`,
    SETTINGS_SEARCH: `${CATEGORIES.SETTINGS} Search`,
    SETTINGS_SERVICES: `${CATEGORIES.SETTINGS} Services`,
    SETTINGS_TEAM: `${CATEGORIES.SETTINGS} Team`,
  },
  SHARED: {
    RESET: `${CATEGORIES.PASSWORD_RESET}`,
  },
} as const;

export const PATHS = {
  APP: {
    ASSESSMENT_ELECTIVE_REHAB_INFO: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/elective-rehab-info`,
    ASSESSMENT_ELECTIVE_REHAB_MEDICAL: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/elective-rehab-medical`,
    ASSESSMENT_HOME_CARE_INFO: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/home-care-info`,
    ASSESSMENT_HOME_CARE_SEARCH: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/home-care-search`,
    ASSESSMENT_INFO: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/info`,
    ASSESSMENT_MEDICAL: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/medical`,
    ASSESSMENT_MEDICAL_SUPPLIES_CATALOGUE: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/medical-supplies-catalogue`,
    ASSESSMENT_MEDICAL_SUPPLIES_INFO: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/medical-supplies-info`,
    ASSESSMENT_MENTAL: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/mental`,
    ASSESSMENT_MOBILE_SHORT: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/mobile-short`,
    ASSESSMENT_MOBILITY: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/mobility`,
    ASSESSMENT_NEEDS: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/needs`,
    ASSESSMENT_PSYCH_EVALUATION: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/psych-evaluation`,
    ASSESSMENT_PSYCH_INFO: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/psych-info`,
    ASSESSMENT_STEP: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/step`,
    ASSESSMENT_TRANSFER: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/transfer`,
    ASSESSMENT_TRANSPORT: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/transport`,
    ASSESSMENT_TRANSPORT_INFO: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/transport-info`,
    ASSESSMENT_TRANSPORT_SEARCH: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/assessing/transport-search`,
    AUTH_LOGIN: `/auth`,
    AUTH_PASSWORD_RESET: `/app/reset/${PARAM}`,
    AUTH_SSO_LOGIN: `/auth/${PARAMS.TOKEN}`,
    CHAT: `/app/clinic/dashboard/chat`,
    CHAT_ID: `/app/clinic/dashboard/chat/${PARAMS.CHAT_ID}`,
    CLINIC_CHANGE_PASSWORD: "/app/clinic/dashboard/my-password-change",
    CLINIC_DEBUG_ON_PREMISE_IMPORTS:
      "/app/clinic/dashboard/onpremsetup/imports",
    CLINIC_DEBUG_ON_PREMISE_INSTALL:
      "/app/clinic/dashboard/onpremsetup/install",
    CLINIC_DEBUG_ON_PREMISE_TEST: "/app/clinic/dashboard/onpremsetup/test",
    CLINIC_REPORT: "/app/clinic/dashboard/report",
    CLINIC_USERS: "/app/clinic/dashboard/users",
    DASHBOARD_ACTIVE: "/app/clinic/dashboard/todo/active",
    DASHBOARD_ARCHIVED: "/app/clinic/dashboard/todo/archived",
    DASHBOARD_DRAFT: "/app/clinic/dashboard/todo/draft",
    DASHBOARD_FINISHED: "/app/clinic/dashboard/todo/finished",
    DASHBOARD_PRINTABLE: `/app/clinic/${PARAMS.CARESEEKER_ID}/dashboard-print`,
    PATIENT_BARTHEL_INDEX: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/barthel-index`,
    PATIENT_CREATE: "/app/clinic/dashboard/patient/new",
    PATIENT_DISCHARGE_PRINT: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/discharge`,
    PATIENT_DOCS_REHAB_FORMS_HOME: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/files/rehab-forms`,
    PATIENT_EARLY_REHAB_INDEX: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/early-rehab-index`,
    PATIENT_FILES: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/files`,
    PATIENT_INSURANCE: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/insurance`,
    PATIENT_MAP: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/map`,
    PATIENT_PROFILE: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/profile`,
    PATIENT_REHAB_FORMS: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/rehab-forms/${PARAMS.FORM}`,
    PATIENT_REHAB_FORMS_HOME: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/rehab-forms`,
    PATIENT_SEARCH: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/search`,
    PATIENT_SEARCH_PANEL_PRINT: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/printable-search-panel`,
    PATIENT_TIMELINE: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/timeline`,
    PATIENT_TRANSITIONAL_CARE: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/transitional`,
    PREDICT: `/app/clinic/dashboard/predict`,
    PREDICT_DETAILS: `/app/clinic/dashboard/predict/${PARAMS.PATIENT_ID}`,
    REHAB_FORM_APPROVAL: `/app/clinic/dashboard/${REHAB_FORM_APPROVAL_PAGE}`,
    REQUEST_DETAILS: `/app/clinic/dashboard/patient/${PARAMS.PATIENT_ID}/auction/${PARAMS.AUCTION_ID}/request/${PARAMS.AUCTION_REQUEST_ID}`,
  },

  PROVIDER_SEARCH: {
    ACCOUNTS: `/providersearch/${PARAMS.PROVIDER_SEARCH}/account/${PARAMS.ACCOUNT_ID}`,
    ACTIVATION_WIZARD: `/providersearch/activationwizard`,
    AUCTION_REQUEST: `/providersearch/${PARAMS.PROVIDER_SEARCH}/request/${PARAMS.AUCTION_REQUEST_ID}`,
    CONSULTANT_DETAILS: `/providersearch/${PARAMS.PROVIDER_SEARCH}/consultant/${PARAMS.CONSULTANT_ID}`,
    CONSULTANT_DETAILS_PRINT: `/providersearch/${PARAMS.PROVIDER_SEARCH}/print-consultant/${PARAMS.CONSULTANT_ID}`,
    GLOSSARY: `/providersearch/${PARAMS.PROVIDER_SEARCH}/glossary/${PARAMS.SECTION}`,
    LISTINGS: `/providersearch/${PARAMS.PROVIDER_SEARCH}/listing`,
    LISTINGS_PRINT: `/providersearch/${PARAMS.PROVIDER_SEARCH}/printable-listing`,
    LOGIN_WIZARD: `/providersearch/loginwizard`,
    LOGOUT: `logout`,
    PATIENT_DASHBOARD: `/providersearch/${PARAMS.PROVIDER_SEARCH}/dashboard/patient/${PARAMS.PATIENT_ID}`,
    PROVIDER_DETAILS: `/providersearch/${PARAMS.PROVIDER_SEARCH}/provider/${PARAMS.CAREPROVIDER_ID}`,
    PROVIDER_DETAILS_PRINT: `/providersearch/${PARAMS.PROVIDER_SEARCH}/print-provider/${PARAMS.CAREPROVIDER_ID}`,
    ZIPCODE_SELECT: `/providersearch/${PARAMS.PROVIDER_SEARCH}`,
  },
  RECEIVER: {
    ADMISSIONS_ACTION_PAGE_STOP_AUCTION: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/${ADMISSIONS_ACTION_SCREENS.STOP_AUCTION}`,
    ADMISSIONS_FORM_1_PERSONAL_INFORMATION: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/step/${ADMISSIONS_STEPS.PERSONAL_INFORMATION}`,
    ADMISSIONS_FORM_2_DESIRED_CARE: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/step/${ADMISSIONS_STEPS.DESIRED_CARE}`,
    ADMISSIONS_FORM_3_CARE_INFORMATION: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/step/${ADMISSIONS_STEPS.CARE_INFORMATION}`,
    ADMISSIONS_FORM_4_FURTHER_INFORMATION: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/step/${ADMISSIONS_STEPS.FURTHER_INFORMATION}`,
    ADMISSIONS_INFO_PAGE_ADD_REQUEST_SUCCESS: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/info/${ADMISSIONS_INFO_SCREENS.FORM_SUBMITTED_SUCCESS}`,
    ADMISSIONS_INFO_PAGE_EMAIL_SENT: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/info/${ADMISSIONS_INFO_SCREENS.LOGIN_EMAIL_SENT}`,
    ADMISSIONS_LOGIN_WIZARD: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/wizard`,
    ADMISSIONS_START_PAGE: `/careprovider/admissions/${PARAMS.ADMISSIONS_ID}/login`,
    AUTH_LOGIN: `/careprovider/auth`,
    AUTH_PASSWORD_RESET: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/reset`,
    CAREPROVIDER_NOTIFICATION_PANEL: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/notification-panel`,
    CAREPROVIDER_PASSWORD_CHANGE: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/my-password-change`,
    CAREPROVIDER_PROFILE: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/profile`,
    CAREPROVIDER_REPORT: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/report/${PARAMS.TAB}`,
    DASHBOARD_ATTRIBUTED: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/attributed`,
    DASHBOARD_DIRECT: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/direct`,
    DASHBOARD_IN_PROGRESS: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/in_progress`,
    DASHBOARD_NEW: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/new`,
    DASHBOARD_OTHER: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/other`,
    OB_PERSONAL_CONGRATS: `/careprovider/newonboarding/personal/${PARAM}/congratulations`,
    OB_PERSONAL_PASSWORD: `/careprovider/newonboarding/personal/${PARAM}/password`,
    OB_PROVIDER_ACCOUNT: `/careprovider/newonboarding/${PARAM}/account`,
    OB_PROVIDER_COMPANY: `/careprovider/newonboarding/${PARAM}/company`,
    OB_PROVIDER_CONGRATS: `/careprovider/newonboarding/${PARAM}/congratulations`,
    OB_PROVIDER_INFO: `/careprovider/newonboarding/${PARAM}/additional-information`,
    OB_PROVIDER_PASSWORD: `/careprovider/newonboarding/${PARAM}/password`,
    OB_PROVIDER_SERVICES: `/careprovider/newonboarding/${PARAM}/services`,
    ONBOARDING_COMPANY: `/careprovider/companyonboarding/${PARAM}`,
    ONBOARDING_PERSONAL: `/careprovider/newonboarding/personal/${PARAM}`,
    ONBOARDING_PROVIDER: `/careprovider/newonboarding/${PARAM}`,
    REQUEST_BARTHEL_INDEX: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/${PARAMS.AUCTION_REQUEST_ID}/barthel-index`,
    REQUEST_DETAILS: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/${PARAMS.AUCTION_REQUEST_ID}`,
    REQUEST_EARLY_REHAB: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/requests/${PARAMS.AUCTION_REQUEST_ID}/early-rehab-index`,
    SETTINGS_ACCOUNT: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/account`,
    SETTINGS_CAPACITY: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/capacity`,
    SETTINGS_EXTENSIONS: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/extensions`,
    SETTINGS_LEGAL: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/legal-documents`,
    SETTINGS_NOTIFICATIONS: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/notifications`,
    SETTINGS_PARTNER_CLINICS: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/partner-clinics`,
    SETTINGS_PROFILE: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/profile`,
    SETTINGS_PWA_BETA: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/pwa`,
    SETTINGS_SEARCH: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/search`,
    SETTINGS_SERVICES: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/services`,
    SETTINGS_TEAM: `/careprovider/dashboard/${PARAMS.CAREPROVIDER_ID}/settings/team`,
  },
  SHARED: {
    RESET: `/reset/${PARAMS.TOKEN}`,
  },
} as const;

export const PATHNAME_MAPPING = {
  [PATHS.APP.ASSESSMENT_ELECTIVE_REHAB_INFO]:
    PAGES.APP.ASSESSMENT_ELECTIVE_REHAB_INFO,
  [PATHS.APP.ASSESSMENT_ELECTIVE_REHAB_MEDICAL]:
    PAGES.APP.ASSESSMENT_ELECTIVE_REHAB_MEDICAL,
  [PATHS.APP.ASSESSMENT_HOME_CARE_INFO]: PAGES.APP.ASSESSMENT_HOME_CARE_INFO,
  [PATHS.APP.ASSESSMENT_HOME_CARE_SEARCH]:
    PAGES.APP.ASSESSMENT_HOME_CARE_SEARCH,
  [PATHS.APP.ASSESSMENT_INFO]: PAGES.APP.ASSESSMENT_INFO,
  [PATHS.APP.ASSESSMENT_MEDICAL]: PAGES.APP.ASSESSMENT_MEDICAL,
  [PATHS.APP.ASSESSMENT_MEDICAL_SUPPLIES_CATALOGUE]:
    PAGES.APP.ASSESSMENT_MEDICAL_SUPPLIES_CATALOGUE,
  [PATHS.APP.ASSESSMENT_MEDICAL_SUPPLIES_INFO]:
    PAGES.APP.ASSESSMENT_MEDICAL_SUPPLIES_INFO,
  [PATHS.APP.ASSESSMENT_MENTAL]: PAGES.APP.ASSESSMENT_MENTAL,
  [PATHS.APP.ASSESSMENT_MOBILE_SHORT]: PAGES.APP.ASSESSMENT_MOBILE_SHORT,
  [PATHS.APP.ASSESSMENT_MOBILITY]: PAGES.APP.ASSESSMENT_MOBILITY,
  [PATHS.APP.ASSESSMENT_NEEDS]: PAGES.APP.ASSESSMENT_NEEDS,
  [PATHS.APP.ASSESSMENT_PSYCH_EVALUATION]:
    PAGES.APP.ASSESSMENT_PSYCH_EVALUATION,
  [PATHS.APP.ASSESSMENT_PSYCH_INFO]: PAGES.APP.ASSESSMENT_PSYCH_INFO,
  [PATHS.APP.ASSESSMENT_STEP]: PAGES.APP.ASSESSMENT_STEP,
  [PATHS.APP.ASSESSMENT_TRANSFER]: PAGES.APP.ASSESSMENT_TRANSFER,
  [PATHS.APP.ASSESSMENT_TRANSPORT]: PAGES.APP.ASSESSMENT_TRANSPORT,
  [PATHS.APP.ASSESSMENT_TRANSPORT_INFO]: PAGES.APP.ASSESSMENT_TRANSPORT_INFO,
  [PATHS.APP.ASSESSMENT_TRANSPORT_SEARCH]:
    PAGES.APP.ASSESSMENT_TRANSPORT_SEARCH,
  [PATHS.APP.AUTH_LOGIN]: PAGES.APP.AUTH_LOGIN,
  [PATHS.APP.AUTH_PASSWORD_RESET]: PAGES.APP.AUTH_PASSWORD_RESET,
  [PATHS.APP.AUTH_SSO_LOGIN]: PAGES.APP.AUTH_SSO_LOGIN,
  [PATHS.APP.CHAT]: PAGES.APP.CHAT,
  [PATHS.APP.CHAT_ID]: PAGES.APP.CHAT_ID,
  [PATHS.APP.CLINIC_CHANGE_PASSWORD]: PAGES.APP.CLINIC_CHANGE_PASSWORD,
  [PATHS.APP.CLINIC_DEBUG_ON_PREMISE_IMPORTS]:
    PAGES.APP.CLINIC_DEBUG_ON_PREMISE_IMPORTS,
  [PATHS.APP.CLINIC_DEBUG_ON_PREMISE_INSTALL]:
    PAGES.APP.CLINIC_DEBUG_ON_PREMISE_INSTALL,
  [PATHS.APP.CLINIC_DEBUG_ON_PREMISE_TEST]:
    PAGES.APP.CLINIC_DEBUG_ON_PREMISE_TEST,
  [PATHS.APP.CLINIC_REPORT]: PAGES.APP.CLINIC_REPORT,
  [PATHS.APP.CLINIC_USERS]: PAGES.APP.CLINIC_USERS,
  [PATHS.APP.DASHBOARD_ACTIVE]: PAGES.APP.DASHBOARD_ACTIVE,
  [PATHS.APP.DASHBOARD_ARCHIVED]: PAGES.APP.DASHBOARD_ARCHIVED,
  [PATHS.APP.DASHBOARD_DRAFT]: PAGES.APP.DASHBOARD_DRAFT,
  [PATHS.APP.DASHBOARD_FINISHED]: PAGES.APP.DASHBOARD_FINISHED,
  [PATHS.APP.DASHBOARD_PRINTABLE]: PAGES.APP.DASHBOARD_PRINTABLE,
  [PATHS.APP.PATIENT_BARTHEL_INDEX]: PAGES.APP.PATIENT_BARTHEL_INDEX,
  [PATHS.APP.PATIENT_CREATE]: PAGES.APP.PATIENT_CREATE,
  [PATHS.APP.PATIENT_DISCHARGE_PRINT]: PAGES.APP.PATIENT_DISCHARGE_PRINT,
  [PATHS.APP.PATIENT_DOCS_REHAB_FORMS_HOME]:
    PAGES.APP.PATIENT_DOCS_REHAB_FORMS_HOME,
  [PATHS.APP.PATIENT_EARLY_REHAB_INDEX]: PAGES.APP.PATIENT_EARLY_REHAB_INDEX,
  [PATHS.APP.PATIENT_FILES]: PAGES.APP.PATIENT_FILES,
  [PATHS.APP.PATIENT_INSURANCE]: PAGES.APP.PATIENT_INSURANCE,
  [PATHS.APP.PATIENT_MAP]: PAGES.APP.PATIENT_MAP,
  [PATHS.APP.PATIENT_PROFILE]: PAGES.APP.PATIENT_PROFILE,
  [PATHS.APP.PATIENT_REHAB_FORMS]: PAGES.APP.PATIENT_REHAB_FORMS,
  [PATHS.APP.PATIENT_REHAB_FORMS_HOME]: PAGES.APP.PATIENT_REHAB_FORMS_HOME,
  [PATHS.APP.PATIENT_SEARCH]: PAGES.APP.PATIENT_SEARCH,
  [PATHS.APP.PATIENT_SEARCH_PANEL_PRINT]: PAGES.APP.PATIENT_SEARCH_PANEL_PRINT,
  [PATHS.APP.PATIENT_TIMELINE]: PAGES.APP.PATIENT_TIMELINE,
  [PATHS.APP.PATIENT_TRANSITIONAL_CARE]: PAGES.APP.PATIENT_TRANSITIONAL_CARE,
  [PATHS.APP.PREDICT]: PAGES.APP.PREDICT,
  [PATHS.APP.PREDICT_DETAILS]: PAGES.APP.PREDICT_DETAILS,
  [PATHS.APP.REHAB_FORM_APPROVAL]: PAGES.APP.REHAB_FORM_APPROVAL,
  [PATHS.APP.REQUEST_DETAILS]: PAGES.APP.REQUEST_DETAILS,
  [PATHS.PROVIDER_SEARCH.ACCOUNTS]: PAGES.PROVIDER_SEARCH.ACCOUNTS,
  [PATHS.PROVIDER_SEARCH.ACTIVATION_WIZARD]:
    PAGES.PROVIDER_SEARCH.ACTIVATION_WIZARD,
  [PATHS.PROVIDER_SEARCH.AUCTION_REQUEST]:
    PAGES.PROVIDER_SEARCH.AUCTION_REQUEST,
  [PATHS.PROVIDER_SEARCH.CONSULTANT_DETAILS]:
    PAGES.PROVIDER_SEARCH.CONSULTANT_DETAILS,
  [PATHS.PROVIDER_SEARCH.CONSULTANT_DETAILS_PRINT]:
    PAGES.PROVIDER_SEARCH.CONSULTANT_DETAILS_PRINT,
  [PATHS.PROVIDER_SEARCH.GLOSSARY]: PAGES.PROVIDER_SEARCH.GLOSSARY,
  [PATHS.PROVIDER_SEARCH.LISTINGS]: PAGES.PROVIDER_SEARCH.LISTINGS,
  [PATHS.PROVIDER_SEARCH.LISTINGS_PRINT]: PAGES.PROVIDER_SEARCH.LISTINGS_PRINT,
  [PATHS.PROVIDER_SEARCH.LOGIN_WIZARD]: PAGES.PROVIDER_SEARCH.LOGIN_WIZARD,
  [PATHS.PROVIDER_SEARCH.LOGOUT]: PAGES.PROVIDER_SEARCH.LOGOUT,
  [PATHS.PROVIDER_SEARCH.PATIENT_DASHBOARD]:
    PAGES.PROVIDER_SEARCH.PATIENT_DASHBOARD,
  [PATHS.PROVIDER_SEARCH.PROVIDER_DETAILS]:
    PAGES.PROVIDER_SEARCH.PROVIDER_DETAILS,
  [PATHS.PROVIDER_SEARCH.PROVIDER_DETAILS_PRINT]:
    PAGES.PROVIDER_SEARCH.PROVIDER_DETAILS_PRINT,
  [PATHS.PROVIDER_SEARCH.ZIPCODE_SELECT]: PAGES.PROVIDER_SEARCH.ZIPCODE_SELECT,
  [PATHS.RECEIVER.ADMISSIONS_ACTION_PAGE_STOP_AUCTION]:
    PAGES.RECEIVER.ADMISSIONS_ACTION_PAGE_STOP_AUCTION,
  [PATHS.RECEIVER.ADMISSIONS_FORM_1_PERSONAL_INFORMATION]:
    PAGES.RECEIVER.ADMISSIONS_FORM_1_PERSONAL_INFORMATION,
  [PATHS.RECEIVER.ADMISSIONS_FORM_2_DESIRED_CARE]:
    PAGES.RECEIVER.ADMISSIONS_FORM_2_DESIRED_CARE,
  [PATHS.RECEIVER.ADMISSIONS_FORM_3_CARE_INFORMATION]:
    PAGES.RECEIVER.ADMISSIONS_FORM_3_CARE_INFORMATION,
  [PATHS.RECEIVER.ADMISSIONS_FORM_4_FURTHER_INFORMATION]:
    PAGES.RECEIVER.ADMISSIONS_FORM_4_FURTHER_INFORMATION,
  [PATHS.RECEIVER.ADMISSIONS_INFO_PAGE_ADD_REQUEST_SUCCESS]:
    PAGES.RECEIVER.ADMISSIONS_INFO_PAGE_ADD_REQUEST_SUCCESS,
  [PATHS.RECEIVER.ADMISSIONS_INFO_PAGE_EMAIL_SENT]:
    PAGES.RECEIVER.ADMISSIONS_INFO_PAGE_EMAIL_SENT,
  [PATHS.RECEIVER.ADMISSIONS_LOGIN_WIZARD]:
    PAGES.RECEIVER.ADMISSIONS_LOGIN_WIZARD,
  [PATHS.RECEIVER.ADMISSIONS_START_PAGE]: PAGES.RECEIVER.ADMISSIONS_START_PAGE,
  [PATHS.RECEIVER.AUTH_LOGIN]: PAGES.RECEIVER.AUTH_LOGIN,
  [PATHS.RECEIVER.AUTH_PASSWORD_RESET]: PAGES.RECEIVER.AUTH_PASSWORD_RESET,
  [PATHS.RECEIVER.CAREPROVIDER_NOTIFICATION_PANEL]:
    PAGES.RECEIVER.CAREPROVIDER_NOTIFICATION_PANEL,
  [PATHS.RECEIVER.CAREPROVIDER_PASSWORD_CHANGE]:
    PAGES.RECEIVER.CAREPROVIDER_PASSWORD_CHANGE,
  [PATHS.RECEIVER.CAREPROVIDER_PROFILE]: PAGES.RECEIVER.CAREPROVIDER_PROFILE,
  [PATHS.RECEIVER.CAREPROVIDER_REPORT]: PAGES.RECEIVER.CAREPROVIDER_REPORT,
  [PATHS.RECEIVER.DASHBOARD_ATTRIBUTED]: PAGES.RECEIVER.DASHBOARD_ATTRIBUTED,
  [PATHS.RECEIVER.DASHBOARD_DIRECT]: PAGES.RECEIVER.DASHBOARD_DIRECT,
  [PATHS.RECEIVER.DASHBOARD_IN_PROGRESS]: PAGES.RECEIVER.DASHBOARD_IN_PROGRESS,
  [PATHS.RECEIVER.DASHBOARD_NEW]: PAGES.RECEIVER.DASHBOARD_NEW,
  [PATHS.RECEIVER.DASHBOARD_OTHER]: PAGES.RECEIVER.DASHBOARD_OTHER,
  [PATHS.RECEIVER.OB_PERSONAL_CONGRATS]: PAGES.RECEIVER.OB_PERSONAL_CONGRATS,
  [PATHS.RECEIVER.OB_PERSONAL_PASSWORD]: PAGES.RECEIVER.OB_PERSONAL_PASSWORD,
  [PATHS.RECEIVER.OB_PROVIDER_ACCOUNT]: PAGES.RECEIVER.OB_PROVIDER_ACCOUNT,
  [PATHS.RECEIVER.OB_PROVIDER_COMPANY]: PAGES.RECEIVER.OB_PROVIDER_COMPANY,
  [PATHS.RECEIVER.OB_PROVIDER_CONGRATS]: PAGES.RECEIVER.OB_PROVIDER_CONGRATS,
  [PATHS.RECEIVER.OB_PROVIDER_INFO]: PAGES.RECEIVER.OB_PROVIDER_INFO,
  [PATHS.RECEIVER.OB_PROVIDER_PASSWORD]: PAGES.RECEIVER.OB_PROVIDER_PASSWORD,
  [PATHS.RECEIVER.OB_PROVIDER_SERVICES]: PAGES.RECEIVER.OB_PROVIDER_SERVICES,
  [PATHS.RECEIVER.ONBOARDING_COMPANY]: PAGES.RECEIVER.ONBOARDING_COMPANY,
  [PATHS.RECEIVER.ONBOARDING_PERSONAL]: PAGES.RECEIVER.ONBOARDING_PERSONAL,
  [PATHS.RECEIVER.ONBOARDING_PROVIDER]: PAGES.RECEIVER.ONBOARDING_PROVIDER,
  [PATHS.RECEIVER.REQUEST_BARTHEL_INDEX]: PAGES.RECEIVER.REQUEST_BARTHEL_INDEX,
  [PATHS.RECEIVER.REQUEST_DETAILS]: PAGES.RECEIVER.REQUEST_DETAILS,
  [PATHS.RECEIVER.REQUEST_EARLY_REHAB]: PAGES.RECEIVER.REQUEST_EARLY_REHAB,
  [PATHS.RECEIVER.SETTINGS_ACCOUNT]: PAGES.RECEIVER.SETTINGS_ACCOUNT,
  [PATHS.RECEIVER.SETTINGS_CAPACITY]: PAGES.RECEIVER.SETTINGS_CAPACITY,
  [PATHS.RECEIVER.SETTINGS_EXTENSIONS]: PAGES.RECEIVER.SETTINGS_EXTENSIONS,
  [PATHS.RECEIVER.SETTINGS_LEGAL]: PAGES.RECEIVER.SETTINGS_LEGAL,
  [PATHS.RECEIVER.SETTINGS_NOTIFICATIONS]:
    PAGES.RECEIVER.SETTINGS_NOTIFICATIONS,
  [PATHS.RECEIVER.SETTINGS_PARTNER_CLINICS]:
    PAGES.RECEIVER.SETTINGS_PARTNER_CLINICS,
  [PATHS.RECEIVER.SETTINGS_PROFILE]: PAGES.RECEIVER.SETTINGS_PROFILE,
  [PATHS.RECEIVER.SETTINGS_PWA_BETA]: PAGES.RECEIVER.SETTINGS_PWA_BETA,
  [PATHS.RECEIVER.SETTINGS_SEARCH]: PAGES.RECEIVER.SETTINGS_SEARCH,
  [PATHS.RECEIVER.SETTINGS_SERVICES]: PAGES.RECEIVER.SETTINGS_SERVICES,
  [PATHS.RECEIVER.SETTINGS_TEAM]: PAGES.RECEIVER.SETTINGS_TEAM,
  [PATHS.SHARED.RESET]: PAGES.SHARED.RESET,
} as const;

export const IGNORE_PATHS = [
  "/",
  "/app",
  "/careprovider",
  `/app/clinic/${PARAMS.CARESEEKER_ID}/${REHAB_FORM_APPROVAL_PAGE}`,
];
